// clipboardExtract — pull a single accepted raster image File out of a
// clipboard source. Two entry points cover the focused-button paste
// path (`extractFromClipboardEvent`) and the explicit-button click
// path (`extractFromClipboardApi`). Both surface the same typed error union so the
// draft cargo-item photo row can render one localized inline error.
//
// Note: SVG is detected separately so the UI can show a specific
// "unsupported type" message instead of generic "no image found".
// SVG is rejected because rasterizing/sanitizing untrusted SVG is out
// of scope for this issue.

import { isUnsupportedSvgImage } from './photoCompress.js';

export type ClipboardExtractError =
  | 'unsupported'
  | 'denied'
  | 'empty'
  | 'unsupportedType'
  | 'failed';

export type ClipboardExtractResult =
  | { ok: true; file: File }
  | { ok: false; error: ClipboardExtractError };

export function clipboardFileName(mime: string): string {
  return `clipboard-image.${extForMime(mime)}`;
}

function extForMime(mime: string): string {
  if (mime === 'image/jpeg') return 'jpg';
  const subtype = mime.match(/^image\/([^;]+)/i)?.[1];
  return subtype?.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'bin';
}

export function extractFromClipboardEvent(data: DataTransfer | null): ClipboardExtractResult {
  if (!data) return { ok: false, error: 'empty' };
  const items = Array.from(data.items);
  let sawSvg = false;
  for (const item of items) {
    if (item.kind !== 'file') continue;
    if (item.type !== '' && !item.type.toLowerCase().startsWith('image/')) continue;
    const original = item.getAsFile();
    if (!original) continue;
    const type = item.type || original.type;
    if (isUnsupportedSvgImage({ type, name: original.name })) {
      sawSvg = true;
      continue;
    }
    return {
      ok: true,
      file: new File([original], item.type ? clipboardFileName(item.type) : original.name, {
        type,
      }),
    };
  }
  return { ok: false, error: sawSvg ? 'unsupportedType' : 'empty' };
}

export async function extractFromClipboardApi(): Promise<ClipboardExtractResult> {
  const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (!clipboard || typeof clipboard.read !== 'function') {
    return { ok: false, error: 'unsupported' };
  }
  let items: readonly ClipboardItem[];
  try {
    items = await clipboard.read();
  } catch (err) {
    return { ok: false, error: isNotAllowed(err) ? 'denied' : 'failed' };
  }
  let sawSvg = false;
  for (const item of items) {
    const mime = item.types.find(
      (type) => type.toLowerCase().startsWith('image/') && !isUnsupportedSvgImage({ type }),
    );
    if (mime) {
      try {
        const blob = await item.getType(mime);
        return {
          ok: true,
          file: new File([blob], clipboardFileName(mime), { type: mime }),
        };
      } catch {
        return { ok: false, error: 'failed' };
      }
    }
    if (item.types.some((type) => isUnsupportedSvgImage({ type }))) sawSvg = true;
  }
  return { ok: false, error: sawSvg ? 'unsupportedType' : 'empty' };
}

function isNotAllowed(err: unknown): boolean {
  if (err instanceof Error) {
    return err.name === 'NotAllowedError' || err.name === 'SecurityError';
  }
  return false;
}
